<template>
    <b-modal :id="id" v-model="visible" size="xl" title="Split Document" @show="visibleToggled" @ok="okClicked"
        scrollable hide-header-close>
        <b-row v-if="imageSrc?.length" class="source">
            <b-col>
                <b-row align-v="center" class="mb-2 selection-toolbar">
                    <b-col cols="auto">
                        <h6 class="mb-0">Source Pages</h6>
                    </b-col>
                    <b-col cols="auto" class="ml-auto d-flex align-items-center">
                        <span class="text-muted mr-3" :class="{ 'invisible': !selectedImages.length }">{{ selectedImages.length || 0 }} of {{ imageSrc?.length }} selected</span>
                        <b-dropdown text="Move Selected To" size="sm" variant="primary" class="mr-3" :class="{ 'invisible': !selectedImages.length }">
                            <b-dropdown-item v-for="(group, idx) in groups" :key="'move-to-' + idx" @click="moveSelectedToGroup(idx)">
                                Group {{ idx + 1 }}{{ group.destination[0] ? ' - ' + group.destination[0] : '' }}
                            </b-dropdown-item>
                        </b-dropdown>
                        <b-form-checkbox
                            :checked="selectedImages.length === imageSrc?.length && imageSrc?.length > 0"
                            :indeterminate="selectedImages.length > 0 && selectedImages.length < (imageSrc?.length ?? 0)"
                            @change="$event ? selectAllImages() : clearSelection()">
                            Select All
                        </b-form-checkbox>
                    </b-col>
                </b-row>
                <draggable v-model="imageSrc" :draggable="'.item'" group="pages"
                    :class="'row row-cols-8' + draggableClass" :force-fallback="true"
                    @change="onSourceChange">
                    <template v-if="loading" #header>
                        <b-col cols="12" class="text-center">
                            <b-spinner class="align-middle" />
                        </b-col>
                    </template>
                    <b-col v-for="(img, index) in imageSrc" :key="'image_' + (index - 1)"
                        class="item position-relative" :class="{'selected-image': isSelected(img), 'current-preview': isWideScreen && isCurrentPreview(img)}">
                        <b-form-checkbox
                            class="image-checkbox"
                            :checked="isSelected(img)"
                            @change="toggleImageSelection(img, $event)"
                            @click.native.stop="handleCheckboxClick(img, $event)" />
                        <b-img :src="img.url" thumbnail block
                            @click="toggleImageSelection(img, undefined, $event)"
                            @mouseenter="showPreview(img)"
                            @mouseleave="hidePreview()" />
                    </b-col>
                </draggable>
            </b-col>
        </b-row>
        <b-row id="destination-row">
            <b-col cols="12" v-for="(group, index) in groups" :key="'group_' + (index + 1)">
                <b-row>
                    <b-col>
                        <h6 class="mt-3">
                            Group {{ index + 1 }}
                            <template v-if="group.destination[0]">
                                - {{ group.destination[0] }}
                            </template>
                        </h6>
                    </b-col>
                </b-row>
                <b-row align-v="end">
                    <b-col cols="auto">
                        Destination:
                    </b-col>
                    <b-col cols="auto">
                        <b-form-checkbox-group v-model="groups[index].destination" buttons size="sm">
                            <b-form-checkbox value="Patient Document" @change="filterGroups"
                                :button-variant="variant(index, 'Patient Document')">
                                Patient Document
                            </b-form-checkbox>
                            <b-form-checkbox value="New Script" @change="filterGroups"
                                :button-variant="variant(index, 'New Script')">
                                New Script
                            </b-form-checkbox>
                            <b-form-checkbox value="Discard" @change="filterGroups"
                                :button-variant="variant(index, 'Discard')">
                                Discard
                            </b-form-checkbox>
                            <b-form-checkbox value="Original Script" @change="filterGroups"
                                :button-variant="variant(index, 'Original Script')"
                                :disabled="isOriginalScriptDisabled(index)"
                                :title="getOriginalScriptTooltip(index)">
                                Original Script
                            </b-form-checkbox>
                        </b-form-checkbox-group>
                    </b-col>
                </b-row>
                <b-row v-if="groups[index].destination.includes('Patient Document')" class="mt-1" align-v="baseline">
                    <b-col>
                        <b-input v-model="groups[index].fileName" placeholder="File Name"
                            :state="fileNameInputState(groups[index].fileName)" required />
                    </b-col>
                    <b-col>
                        <search-component :id="groups[index].patient && +groups[index].patient.id"
                            v-model="groups[index].patient" :hide-label="true" placeholder="Patient"
                            search-u-r-l="/Patient" create-new-object clearable createNewLabel="Create New Patient"
                            @createNewObject="groups[index].showPatientForm = true" required
                            :state="patientSearchInputState(groups[index].patient)" />
                    </b-col>
                    <b-col cols="12" v-if="availableTags?.length">
                        <label>Tags<span v-if="tagsRequired"> *</span></label>
                        <multiselect v-model="groups[index].tags" :options="availableTags" multiple />
                    </b-col>

                </b-row>
                <b-row :class="(groups[index].showPatientForm ? 'sticky' : '')">
                    <b-col>
                        <!--suppress HtmlWrongAttributeValue draggable doesn't like .item-->
                        <draggable v-model="groups[index].images" draggable=".item" group="pages"
                            class="w-100 h-100 text-center mt-1 row row-cols-8" style="border: 1px dashed; min-height: 100px;">
                            <template v-if="!groups[index].images.length" #header>
                                <b-col cols="12" class="drop-hint">
                                    Drop pages here.
                                </b-col>
                            </template>
                            <b-col v-for="(img, idx) in groups[index].images"
                                :key="'group_' + index + '_image_' + (idx - 1)" class="item"
                                :class="{ 'current-preview': isWideScreen && isCurrentPreview(img) }"
                                @click="selectedImage = img.url" >
                                <b-img :src="img.url" thumbnail block
                                    @mouseenter="showPreview(img)"
                                    @mouseleave="hidePreview()" />
                            </b-col>
                        </draggable>
                    </b-col>
                </b-row>
                <b-row v-if="groups[index].showPatientForm">
                    <b-col cols="5">
                        <b-img :src="srcForGroup(groups[index])" class="sticky-2"/>
                    </b-col>
                    <b-col v-if="groups[index].showPatientForm">
                        <PatientFormComponent :button-titles="patientButtonTitles" create-new-patient hide-insurance
                            @close="closePatientForm" @created="onPatientCreated" />
                    </b-col>
                </b-row>
            </b-col>

            <b-col v-if="groups.length < (totalPages ?? 0)" cols="12">
                <b-button variant="success" @click="addGroup">
                    Add Group
                </b-button>
            </b-col>
        </b-row>
        <div v-if="displayedPreview" class="preview-panel" :class="{ 'always-visible': isWideScreen }">
            <img :src="previewUrl" class="preview-image" />
            <div class="preview-label">
                Page {{ displayedPreview.pageNumber + 1 }}
                <span v-if="displayedPreview.hiResLoading" class="hi-res-loading">(loading HD...)</span>
            </div>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Vue, Component, Prop, VModel } from 'vue-property-decorator';
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import draggable from 'vuedraggable';
import { BvEvent } from "bootstrap-vue";
import SearchComponent from "@/components/SearchComponent.vue";
import { Patient } from "@/models/Patient";
import { QueueItem } from "@/models/QueueItem";
import PatientFormComponent from "@/components/Patient/PatientFormComponent.vue";
import Multiselect from "vue-multiselect";

type PageImage = {
    pageNumber: number,
    url: string,
    hiResUrl?: string,
    hiResLoading?: boolean,
}
type Group = {
    destination: string[],
    images: PageImage[],
    fileName: string | null,
    tags: string[],
    patient: Patient,
    showPatientForm: boolean,
}
@Component({
    name: "PdfChopperModal",
    components: {
        Multiselect,
        PatientFormComponent,
        SearchComponent,
        draggable,
    },
})
export default class PdfChopperModal extends Vue {
    @VModel() visible!: boolean;
    @Prop({ type: String, default: "pdf-chopper" }) id!: string;
    @Prop() imageId!: number;
    @Prop({ type: Boolean, default: false }) hasSavedPrescription!: boolean;
    protected imageSrc?: PageImage[] = [];
    protected pages: number[] = [];
    protected groups: Group[] = [
        { destination: [], images: [], fileName: null, tags: [], patient: new Patient(), showPatientForm: false },
        { destination: [], images: [], fileName: null, tags: [], patient: new Patient(), showPatientForm: false },
    ];
    protected availableTags: string[] = [];
    private requirePatientDocumentTags = false;

    protected patientButtonTitles = {
        new: "",
        clear: "",
        save: "Save Patient",
        cancel: "Close Patient Form",
    };

    protected selectedImage: string = "";
    protected selectedImages: PageImage[] = [];
    protected isDraggingMultiple = false;
    protected lastSelectedIndex: number | null = null;
    protected hoveredImage: PageImage | null = null;
    protected previewImage: PageImage | null = null;
    private previewTimeout: number | null = null;
    protected isWideScreen = false;
    private resizeHandler: (() => void) | null = null;

    get displayedPreview(): PageImage | null {
        // On wide screens, always show something: current preview, or first page
        if (this.isWideScreen) {
            const preview = this.previewImage || this.imageSrc?.[0] || null;
            // Trigger high-res load when preview changes on wide screens
            if (preview && !preview.hiResUrl && !preview.hiResLoading) {
                this.loadHiResImage(preview);
            }
            return preview;
        }
        // On narrow screens, only show when actively previewing
        return this.previewImage;
    }

    get previewUrl(): string {
        const preview = this.displayedPreview;
        if (!preview) return '';
        // Use high-res URL on wide screens when available
        if (this.isWideScreen && preview.hiResUrl) {
            return preview.hiResUrl;
        }
        return preview.url;
    }

    mounted() {
        this.checkWideScreen();
        this.resizeHandler = () => this.checkWideScreen();
        window.addEventListener('resize', this.resizeHandler);
    }

    beforeDestroy() {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        // Clean up object URLs to prevent memory leaks
        this.cleanupHiResUrls();
    }

    cleanupHiResUrls() {
        const allImages = [
            ...(this.imageSrc || []),
            ...this.groups.flatMap(g => g.images)
        ];
        for (const img of allImages) {
            if (img.hiResUrl) {
                URL.revokeObjectURL(img.hiResUrl);
            }
        }
    }

    checkWideScreen() {
        this.isWideScreen = window.innerWidth >= 1600;
    }

    async loadHiResImage(img: PageImage) {
        if (img.hiResUrl || img.hiResLoading) return;

        // Mark as loading
        this.$set(img, 'hiResLoading', true);

        try {
            const response = await axios.get(`/api/image/${this.imageId}/page/${img.pageNumber}?height=1500`, {
                responseType: 'blob'
            });
            const blob = response.data;
            const hiResUrl = URL.createObjectURL(blob);
            this.$set(img, 'hiResUrl', hiResUrl);
        } catch (err) {
            console.error('Failed to load high-res image:', err);
        } finally {
            this.$set(img, 'hiResLoading', false);
        }
    }

    showPreview(img: PageImage) {
        this.hoveredImage = img;
        if (this.previewTimeout) {
            clearTimeout(this.previewTimeout);
        }
        // On wide screens, show preview immediately; on narrow screens, delay
        const delay = this.isWideScreen ? 0 : 750;
        this.previewTimeout = window.setTimeout(() => {
            this.previewImage = this.hoveredImage;
        }, delay);
    }

    hidePreview() {
        this.hoveredImage = null;
        if (this.previewTimeout) {
            clearTimeout(this.previewTimeout);
            this.previewTimeout = null;
        }
        // On wide screens, keep showing the last previewed image
        if (!this.isWideScreen) {
            this.previewImage = null;
        }
    }

    closePatientForm() {
        this.groups.forEach(g => g.showPatientForm = false);
    }

    onPatientCreated(newPatient: Patient) {
        this.groups.forEach(g => {
            if (g.showPatientForm) {
                g.patient = newPatient;
                g.showPatientForm = false;
            }
        });
    }

    protected loading: boolean = false;
    private shouldValidate = false;
    private isProcessing: boolean = false;

    created() {
        console.log("%c Created PdfChopperModal", "color: green;");
        this.getTags();
        this.getConfiguration();
    }

    filterGroups() {
        this.groups = this.groups.map(g => ({
            destination: [g.destination[g.destination.length - 1]],
            images: g.images,
            fileName: g.fileName,
            tags: g.tags,
            patient: g.patient,
            showPatientForm: g.showPatientForm,
        }));
    }

    addGroup() {
        this.groups.push({
            destination: [],
            images: [],
            fileName: null,
            tags: [],
            patient: new Patient(),
            showPatientForm: false
        });
    }

    handleCheckboxClick(img: PageImage, event: MouseEvent): void {
        // Handle shift+click for range selection on checkbox
        if (event.shiftKey && this.lastSelectedIndex !== null && this.imageSrc) {
            const currentIndex = this.imageSrc.findIndex(i => i.pageNumber === img.pageNumber);
            if (currentIndex !== -1) {
                event.preventDefault();
                const start = Math.min(this.lastSelectedIndex, currentIndex);
                const end = Math.max(this.lastSelectedIndex, currentIndex);
                const rangeImages = this.imageSrc.slice(start, end + 1);

                // Add all images in range to selection (without duplicates)
                for (const rangeImg of rangeImages) {
                    if (!this.selectedImages.some(i => i.pageNumber === rangeImg.pageNumber)) {
                        this.selectedImages.push(rangeImg);
                    }
                }
            }
        }
    }

    toggleImageSelection(img: PageImage, e?: boolean, event?: MouseEvent): void {
        const currentIndex = this.imageSrc?.findIndex(i => i.pageNumber === img.pageNumber) ?? -1;

        // Handle shift+click for range selection
        if (event?.shiftKey && this.lastSelectedIndex !== null && currentIndex !== -1 && this.imageSrc) {
            const start = Math.min(this.lastSelectedIndex, currentIndex);
            const end = Math.max(this.lastSelectedIndex, currentIndex);
            const rangeImages = this.imageSrc.slice(start, end + 1);

            // Add all images in range to selection (without duplicates)
            for (const rangeImg of rangeImages) {
                if (!this.selectedImages.some(i => i.pageNumber === rangeImg.pageNumber)) {
                    this.selectedImages.push(rangeImg);
                }
            }
            return;
        }

        const hasItem = this.selectedImages.some(i => i.pageNumber === img.pageNumber);
        this.selectedImages = this.selectedImages.filter(i => i.pageNumber !== img.pageNumber);
        if (e || (e === undefined && !hasItem)) {
            this.selectedImages.push(img);
            this.lastSelectedIndex = currentIndex;
        } else {
            // If deselecting, don't update lastSelectedIndex
        }
    }

    isSelected(img: PageImage): boolean {
        return this.selectedImages.some(i => i.pageNumber === img.pageNumber);
    }

    isCurrentPreview(img: PageImage): boolean {
        return this.displayedPreview?.pageNumber === img.pageNumber;
    }

    selectAllImages(): void {
        this.selectedImages = [...(this.imageSrc ?? [])];
    }

    clearSelection(): void {
        this.selectedImages = [];
        this.lastSelectedIndex = null;
    }

    moveSelectedToGroup(groupIndex: number): void {
        if (!this.selectedImages.length) return;
        // Add selected images to target group
        this.groups[groupIndex].images.push(...this.selectedImages);
        // Remove from source
        this.imageSrc = this.imageSrc?.filter(
            img => !this.selectedImages.some(s => s.pageNumber === img.pageNumber)
        );
        this.clearSelection();
    }

    onSourceChange(evt: any): void {
        // If an item was removed and it was selected, move all selected
        if (evt.removed && this.isSelected(evt.removed.element) && this.selectedImages.length > 1) {
            // Find which group received the item
            for (let i = 0; i < this.groups.length; i++) {
                const idx = this.groups[i].images.findIndex(
                    img => img.pageNumber === evt.removed.element.pageNumber
                );
                if (idx >= 0) {
                    // Move remaining selected images to this group (excluding the one already moved)
                    const remainingSelected = this.selectedImages.filter(
                        s => s.pageNumber !== evt.removed.element.pageNumber
                    );
                    this.groups[i].images.push(...remainingSelected);
                    this.imageSrc = this.imageSrc?.filter(
                        img => !remainingSelected.some(s => s.pageNumber === img.pageNumber)
                    );
                    this.clearSelection();
                    break;
                }
            }
        }
    }

    visibleToggled() {
        this.groups = [];
        this.addGroup();
        this.addGroup();
        this.selectedImages = [];
        this.lastSelectedIndex = null;

        this.fetchImageSource();
    }

    variant(index: number, value: string): string {
        if (this.groups[index] == undefined || this.groups[index].destination == undefined) {
            console.log("secondary: undefined!!", index, value, this.groups[index]);
            return "secondary";
        }
        if (this.groups[index].destination.includes(value)) {
            return "success";
        }

        if (this.shouldValidate && this.groups[index].images.length) {
            if ((this.groups[index].destination?.length ?? 0) === 0) {
                return "danger";
            }
            if (this.groups[index].destination[0] === undefined) {
                return "danger";
            }
        }
        return "secondary";
    }

    isOriginalScriptDisabled(index: number): boolean {
        if (!this.hasSavedPrescription) return true;
        return this.groups.some((g, i) => i !== index && g.destination.includes("Original Script"));
    }

    getOriginalScriptTooltip(index: number): string {
        if (!this.hasSavedPrescription) return "Save the prescription first to keep pages with it";
        if (this.groups.some((g, i) => i !== index && g.destination.includes("Original Script"))) {
            return "Only one group can keep pages with the original";
        }
        return "";
    }

    srcForGroup(group: any) {
        if (this.selectedImage?.length > 0) return this.selectedImage;
        return group.images[0]?.url;
    }

    get draggableClass(): string {
        if (this.shouldValidate && !this.imageSrc?.length) return " is-invalid";
        return "";
    }

    patientSearchInputState(patient: Patient): boolean | null {
        if (!this.shouldValidate) return null;
        return !!patient?.id;
    }

    fileNameInputState(fileName: string | null): boolean | null {
        if (!this.shouldValidate) return null;
        return !!fileName?.length;
    }

    validate(): boolean {
        this.shouldValidate = true;
        if (this.imageSrc?.length ?? 0 > 0) {
            this.$notification(NotificationOptions.error("You must move all pages to a group."));
            return false;
        }

        const hasIncompletePatientDocuments = this.groups.some(g => g.destination.includes("Patient Document") && g.images.length > 0 &&
            (g.fileName == null || g.fileName.trim() === "" || g.patient == null || g.patient.id == null || g.patient.id === 0));
        if (hasIncompletePatientDocuments) {
            this.$notification(NotificationOptions.error("Patient Document sections must have a patient and file name specified."));
            return false;
        }

        const hasIncompleteDestinations = this.groups.some(g => g.images.length > 0 && (g.destination.length === 0 || g.destination[0] === undefined));
        if (hasIncompleteDestinations) {
            this.$notification(NotificationOptions.error("You must specify a destination for each group with pages in it."));
            return false;
        }

        // Check if tags are required and missing for Patient Document groups
        if (this.tagsRequired) {
            const hasMissingTags = this.groups.some(g => g.destination.includes("Patient Document") && g.images.length > 0 &&
                (!g.tags || g.tags.length === 0));
            if (hasMissingTags) {
                this.$notification(NotificationOptions.error("Patient Document sections must have at least one tag selected."));
                return false;
            }
        }

        return true;
    }

    async okClicked(event: BvEvent) {
        event.preventDefault();

        if (this.isProcessing) return;  // Guard against multiple clicks

        if (!this.validate()) return;

        this.isProcessing = true;
        try {
            type SplitResponse = {
                newQueueItems: QueueItem[];
                originalScriptUsed: boolean;
                newImageId: number | null;
            };
            const response = await axios.post<SplitResponse>(`/api/image/${this.imageId}/split`, this.groups);
            this.$emit("splitComplete", response.data);
            if ((event.vueTarget as any).hide) {
                (event.vueTarget as any).hide();
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isProcessing = false;
        }
    }

    async getTags() {
        try {
            const response = await axios.get("/api/Configuration/PatientDocumentTags");
            this.availableTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    async getConfiguration() {
        try {
            const response = await axios.get("/api/Configuration/RequirePatientDocumentTags");
            this.requirePatientDocumentTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    get tagsRequired() {
        return this.requirePatientDocumentTags && this.availableTags.length > 0;
    }

    protected totalPages = 0;

    async fetchImageSource() {
        try {
            this.shouldValidate = false;
            this.loading = true;
            const imageUrl = `/api/image/${this.imageId}/pages`;
            this.imageSrc = [];

            const imageResp = await axios.get<[]>(imageUrl);
            for (let i = 0; i < imageResp.data.length; i++) {
                const blob = imageResp.data[i];
                this.imageSrc.push({ pageNumber: i, url: `data:image/webp;base64,${blob}` });
            }
            this.totalPages = this.imageSrc?.length ?? 0;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
        this.loading = false;
    }

}
</script>

<style scoped>
.item {
    margin-bottom: 1rem;
}

.selection-toolbar {
    min-height: 38px;
}

.selection-toolbar ::v-deep .dropdown-menu {
    z-index: 1100;
}

.row-cols-8 > * {
    flex: 0 0 12.5%;
    max-width: 12.5%;
}

.row-cols-8 > .drop-hint {
    flex: 0 0 100%;
    max-width: 100%;
}

.preview-panel {
    position: fixed;
    bottom: 16px;
    right: 16px;
    z-index: 1100;
    background: white;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    padding: 8px;
    max-width: 700px;
    max-height: 85vh;
    pointer-events: none;
}

.preview-image {
    max-width: 100%;
    max-height: calc(85vh - 40px);
    display: block;
}

/* Wide screen: center preview in the reserved 720px space */
@media (min-width: 1600px) {
    .preview-panel {
        top: 50%;
        bottom: auto;
        right: 16px;
        transform: translateY(-50%);
        width: 688px;
        max-width: 688px;
        max-height: 90vh;
    }

    .preview-image {
        max-height: calc(90vh - 60px);
        width: 100%;
        object-fit: contain;
    }
}

.preview-label {
    text-align: center;
    padding-top: 8px;
    font-weight: 500;
    color: #495057;
}

.hi-res-loading {
    font-size: 0.85em;
    color: #6c757d;
    font-weight: normal;
}

/*noinspection CssUnusedSymbol*/
.is-invalid {
    border: 1px solid #dc3545;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right calc(0.375em + 0.1875rem) center;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.source {
    position: sticky;
    top: -24px;
    z-index: 300;
    padding: 10px 0;
    background: white;
}
.sticky {
    position: sticky;
    top: -24px;
    z-index: 300;
    padding: 10px 0;
    background: white;
}
.sticky-2 {
    position: sticky;
    top: 120px;
    z-index: 200;
    padding: 10px 0;
    background: white;
    width: 100%;
}

.selected-image ::v-deep .img-thumbnail {
    outline: 3px solid #007bff;
    outline-offset: -3px;
    background-color: rgba(0, 123, 255, 0.1);
}

.current-preview ::v-deep .img-thumbnail {
    box-shadow: 0 0 0 3px #28a745;
}

.current-preview::after {
    content: '';
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #28a745;
}

.image-checkbox {
    position: absolute;
    top: 8px;
    left: 12px;
    z-index: 1001;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: auto;
}

.item:hover .image-checkbox,
.selected-image .image-checkbox {
    opacity: 1;
}

.image-checkbox ::v-deep .custom-control-label::before {
    background-color: rgba(255, 255, 255, 0.9);
    border: 2px solid #6c757d;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.image-checkbox ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #007bff;
    border-color: #007bff;
}

</style>

<!-- Unscoped styles for modal positioning (Bootstrap Vue modals are portaled to body) -->
<style>
/* Wide screen layout: position modal to the left, reserving space for preview panel */
@media (min-width: 1600px) {
    #pdf-choppa .modal-dialog {
        max-width: calc(100vw - 740px) !important;
        margin-left: 20px !important;
        margin-right: 720px !important;
    }
}
</style>
